import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Search, Filter, AlertTriangle, CheckCircle, Clock, DollarSign,
  Calendar, X, ChevronLeft, ChevronRight, Upload, Eye, Bell
} from 'lucide-react';
import * as fieldTeamApi from '../../services/fieldTeamApi';
import './FieldTeam.css';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  paid: { label: 'Paid', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  partial: { label: 'Partial', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  overdue: { label: 'Overdue', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: 'rgba(107,114,128,0.15)' }
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(null);
  const [showClearanceModal, setShowClearanceModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [dueSoon, setDueSoon] = useState([]);
  const [overdueList, setOverdueList] = useState([]);
  const limit = 20;

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fieldTeamApi.getMyInvoices({ status: statusFilter, overdue_only: overdueOnly, page, limit });
      setInvoices(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [page, statusFilter, overdueOnly]);

  const fetchAlerts = useCallback(async () => {
    try {
      const [ds, od] = await Promise.all([fieldTeamApi.getDueSoonInvoices(), fieldTeamApi.getOverdueInvoices()]);
      setDueSoon(ds.data?.data || []);
      setOverdueList(od.data?.data || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fieldTeamApi.getMyClients({ limit: 100 });
      setClients(res.data?.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchAlerts(); fetchClients(); }, []);

  const handleCreate = async (formData) => {
    try {
      await fieldTeamApi.createInvoice(formData);
      setShowCreateModal(false);
      fetchInvoices();
      fetchAlerts();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const handleFollowUp = async (invoiceId, data) => {
    try {
      await fieldTeamApi.setInvoiceFollowUp(invoiceId, data);
      setShowFollowUpModal(null);
      fetchInvoices();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const handleClearance = async (data) => {
    try {
      await fieldTeamApi.submitClearance(data);
      setShowClearanceModal(null);
      fetchInvoices();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="ft-page">
      {/* Stat Blocks */}
      <div className="stat-blocks">
        <div className="stat-block">
          <div className="stat-icon"><FileText size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{total}</span>
            <span className="stat-label">Total Invoices</span>
          </div>
        </div>
        <div className="stat-block" style={{ '--accent': '#f59e0b' }}>
          <div className="stat-icon"><Clock size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{dueSoon.length}</span>
            <span className="stat-label">Due Soon</span>
          </div>
        </div>
        <div className="stat-block" style={{ '--accent': '#ef4444' }}>
          <div className="stat-icon"><AlertTriangle size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{overdueList.length}</span>
            <span className="stat-label">Overdue</span>
          </div>
        </div>
        <div className="stat-block" style={{ '--accent': '#22c55e' }}>
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{invoices.filter(i => i.status === 'paid').length}</span>
            <span className="stat-label">Paid</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="input" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ width: '160px' }}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="overdue">Overdue</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={overdueOnly} onChange={e => { setOverdueOnly(e.target.checked); setPage(1); }} />
          Overdue Only
        </label>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + New Invoice
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="ft-table-wrapper">
        <table className="ft-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Invoice #</th>
              <th>Amount</th>
              <th>Issue Date</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Paid</th>
              <th>Days Overdue</th>
              <th>Follow-Up</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No invoices found</td></tr>
            ) : invoices.map(inv => {
              const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
              const daysOverdue = inv.days_overdue > 0 ? inv.days_overdue : 0;
              return (
                <tr key={inv.id}>
                  <td>{inv.company_name || '-'}</td>
                  <td>{inv.invoice_number || `#${inv.id}`}</td>
                  <td style={{ fontWeight: 600 }}>{inv.currency} {parseFloat(inv.invoice_amount).toLocaleString()}</td>
                  <td>{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : '-'}</td>
                  <td>{new Date(inv.due_date).toLocaleDateString()}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                      color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                  </td>
                  <td>{inv.currency} {parseFloat(inv.paid_amount || 0).toLocaleString()}</td>
                  <td style={{ color: daysOverdue > 0 ? '#ef4444' : 'var(--text-muted)' }}>
                    {daysOverdue > 0 ? `${daysOverdue}d` : '-'}
                  </td>
                  <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {inv.follow_up_date ? new Date(inv.follow_up_date).toLocaleDateString() : '-'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-sm" title="View" onClick={() => setSelectedInvoice(inv)}><Eye size={14} /></button>
                      <button className="btn btn-sm" title="Set Follow-Up" onClick={() => setShowFollowUpModal(inv)}><Bell size={14} /></button>
                      {inv.status !== 'paid' && (
                        <button className="btn btn-sm" title="Request Clearance" onClick={() => setShowClearanceModal(inv)}><Upload size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
          <span style={{ padding: '6px 12px', fontSize: '13px' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
        </div>
      )}

      {/* Follow-Up Modal */}
      {showFollowUpModal && <FollowUpModal invoice={showFollowUpModal} onClose={() => setShowFollowUpModal(null)} onSave={handleFollowUp} />}

      {/* Clearance Modal */}
      {showClearanceModal && <ClearanceModal invoice={showClearanceModal} onClose={() => setShowClearanceModal(null)} onSave={handleClearance} />}

      {/* Create Invoice Modal */}
      {showCreateModal && <CreateInvoiceModal clients={clients} onClose={() => setShowCreateModal(false)} onSave={handleCreate} />}

      {/* Detail Modal */}
      {selectedInvoice && <InvoiceDetailModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />}
    </div>
  );
}

// ===== Follow-Up Modal =====
function FollowUpModal({ invoice, onClose, onSave }) {
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Set Follow-Up</h3><button className="close-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Invoice #{invoice.invoice_number || invoice.id} — {invoice.company_name}
          </p>
          <div className="form-group">
            <label>Follow-Up Date *</label>
            <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Reason / Notes *</label>
            <textarea className="input" rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g., Client out of town, Check being processed..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!date || !notes} onClick={() => onSave(invoice.id, { follow_up_date: date, follow_up_notes: notes })}>
            Save Follow-Up
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Clearance Modal =====
function ClearanceModal({ invoice, onClose, onSave }) {
  const [amount, setAmount] = useState(parseFloat(invoice.invoice_amount) - parseFloat(invoice.paid_amount || 0));
  const [receiptUrl, setReceiptUrl] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Request Clearance</h3><button className="close-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Invoice #{invoice.invoice_number || invoice.id} — {invoice.company_name} — Remaining: {invoice.currency} {(parseFloat(invoice.invoice_amount) - parseFloat(invoice.paid_amount || 0)).toLocaleString()}
          </p>
          <div className="form-group">
            <label>Amount Collected *</label>
            <input type="number" className="input" value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Receipt URL / Screenshot Link</label>
            <input type="text" className="input" value={receiptUrl} onChange={e => setReceiptUrl(e.target.value)}
              placeholder="Paste receipt link or upload URL..." />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!amount} onClick={() => onSave({
            field_invoice_id: invoice.id, amount_claimed: amount, receipt_url: receiptUrl, notes
          })}>Submit for Approval</button>
        </div>
      </div>
    </div>
  );
}

// ===== Create Invoice Modal =====
function CreateInvoiceModal({ clients, onClose, onSave }) {
  const [form, setForm] = useState({ field_client_id: '', invoice_number: '', invoice_amount: '', due_date: '', notes: '' });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Create Invoice</h3><button className="close-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <div className="form-group">
            <label>Client *</label>
            <select className="input" value={form.field_client_id} onChange={e => setForm({ ...form, field_client_id: e.target.value })}>
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label>Invoice #</label>
              <input type="text" className="input" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Amount *</label>
              <input type="number" className="input" value={form.invoice_amount} onChange={e => setForm({ ...form, invoice_amount: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label>Due Date *</label>
            <input type="date" className="input" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.field_client_id || !form.invoice_amount || !form.due_date}
            onClick={() => onSave(form)}>Create</button>
        </div>
      </div>
    </div>
  );
}

// ===== Invoice Detail Modal =====
function InvoiceDetailModal({ invoice, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '550px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>Invoice Details</h3><button className="close-btn" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
            <div><strong>Client:</strong> {invoice.company_name}</div>
            <div><strong>Invoice #:</strong> {invoice.invoice_number || `#${invoice.id}`}</div>
            <div><strong>Amount:</strong> {invoice.currency} {parseFloat(invoice.invoice_amount).toLocaleString()}</div>
            <div><strong>Paid:</strong> {invoice.currency} {parseFloat(invoice.paid_amount || 0).toLocaleString()}</div>
            <div><strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString()}</div>
            <div><strong>Status:</strong> {STATUS_CONFIG[invoice.status]?.label}</div>
          </div>
          {invoice.follow_up_date && (
            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px' }}>
              <strong>Follow-Up:</strong> {new Date(invoice.follow_up_date).toLocaleDateString()}
              {invoice.follow_up_notes && <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>{invoice.follow_up_notes}</p>}
            </div>
          )}
          {invoice.clearance_requests?.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Clearance Requests</h4>
              {invoice.clearance_requests.map(cr => (
                <div key={cr.id} style={{ padding: '8px', marginBottom: '6px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Amount: {cr.amount_claimed}</span>
                    <span style={{ color: cr.status === 'approved' ? '#22c55e' : cr.status === 'rejected' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                      {cr.status.toUpperCase()}
                    </span>
                  </div>
                  {cr.notes && <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>{cr.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
